// src/lib/api.js
// src/lib/api.js
import { Capacitor } from "@capacitor/core";

const isNative = Capacitor.isNativePlatform();
const isDemoSite = !isNative && window.location.hostname.includes('demo');

export const API_BASE = isDemoSite
  ? "https://sift-demo-api.azurewebsites.net"
  : (import.meta.env.VITE_API_BASE?.replace(/\/+$/, "") || "https://api-sift-web-chb0fda7esekergc.centralus-01.azurewebsites.net");

/** Prefix a relative path with API_BASE */
export function apiUrl(pathOrUrl) {
  if (!pathOrUrl) return API_BASE;
  
  // If it's already an absolute URL, return as-is
  if (/^https?:\/\//.test(pathOrUrl)) {
    return pathOrUrl;
  }
  
  // Otherwise, prepend API_BASE
  const cleanPath = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${API_BASE}${cleanPath}`;
}

// Alias for backward compatibility
export const url = apiUrl;

/* Open the connection to the API ahead of a request somebody is about to make.

   For the signup composers, where the first thing a person does is ask for a
   draft and then watch a button until it comes back. DNS and TLS to a cold
   host are a few hundred milliseconds of that wait, and they can be spent
   while the person is still reading the screen. `anonymous` because the
   drafting calls go out with credentials omitted, and a preconnect only helps
   a request that shares its credentials mode.

   Returns a cleanup that removes the hint, for use as an effect's return. */
export function preconnectApi() {
  let link;
  try {
    link = document.createElement("link");
    link.rel = "preconnect";
    link.href = new URL(API_BASE).origin;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  } catch { /* a hint, never a requirement */ }
  return () => { try { link?.remove(); } catch { /* noop */ } };
}


/**
 * fetchJson — a strict JSON wrapper around `fetch` for public endpoints.
 *
 * Why: when the backend returns the SPA fallback HTML for an
 * unrecognized API URL (e.g. a route that wasn't deployed yet), `fetch`
 * resolves with `res.ok=true` and the HTML body. A naive
 * `await res.json().catch(() => ({}))` silently swallows the parse
 * error, the calling component renders with empty fields, and the bug
 * looks like "the page just shows nothing" instead of a clear failure.
 *
 * fetchJson defends against that by checking `Content-Type` is JSON
 * before parsing. If it isn't, it throws a meaningful error. If
 * `res.ok` is false, it throws with the parsed body's `error` /
 * `detail` field if available.
 *
 * Returns the parsed JSON body on success.
 */
export async function fetchJson(input, options = {}) {
  const res = await fetch(input, options);
  const contentType = (res.headers.get("Content-Type") || "").toLowerCase();
  const looksLikeJson = contentType.includes("application/json");

  if (!looksLikeJson) {
    if (res.ok) {
      throw new Error(
        `Expected JSON from ${typeof input === "string" ? input : input.url}, got ${contentType || "unknown content-type"}. The endpoint may not be deployed.`
      );
    }
    throw new Error(`HTTP ${res.status} ${res.statusText || ""}`.trim());
  }

  let body;
  try {
    body = await res.json();
  } catch {
    throw new Error("Failed to parse JSON response.");
  }

  if (!res.ok) {
    const message = body?.error || body?.detail || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}


/**
 * openAuthedPdf — fetch an auth-token-protected PDF endpoint and open
 * the bytes in a new tab.
 *
 * Why: the natural pattern of `window.open(url, "_blank")` does NOT
 * carry the bearer token from localStorage, so the backend sees an
 * unauthenticated request and 4xx's. The Azure App Service then
 * rewrites that with its themed default error page, which makes the
 * symptom look like a 404 even though the route is correct.
 *
 * This helper fetches the PDF as a blob via fetchWithAuth, builds an
 * object URL, and opens that. The blob URL is short-lived (revoked
 * after 60s) so it doesn't leak into the user's session storage.
 *
 * Usage:
 *   import { openAuthedPdf } from "@/lib/api";
 *   await openAuthedPdf(`/api/contracts/assignments/${id}/pdf/`);
 */
export async function openAuthedPdf(path) {
  // Pre-open the new tab SYNCHRONOUSLY here — popup blockers only allow
  // window.open() when it happens directly inside a user-gesture handler.
  // The `await fetch` below is an async boundary, so if we waited until
  // after it to call window.open() the browser would block the popup
  // silently and the PDF would never appear.
  //
  // Do NOT pass "noopener"/"noreferrer" here: those window features make
  // window.open() return null, so we'd lose the handle and never be able
  // to navigate the tab to the blob URL — the tab would sit on about:blank
  // forever and the PDF would fall through to the download fallback. The
  // blob URL is same-origin and self-generated, so noopener buys nothing.
  const newTab = window.open("about:blank", "_blank");

  const token =
    localStorage.getItem("access") ||
    localStorage.getItem("bearer") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    null;
  if (!token) {
    if (newTab) newTab.close();
    throw new Error("Not signed in. Refresh and try again.");
  }
  const isJWT = !!localStorage.getItem("access");
  const authHeader = isJWT ? `Bearer ${token}` : `Token ${token}`;

  try {
    const res = await fetch(apiUrl(path), {
      headers: { Authorization: authHeader, Accept: "application/pdf" },
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        msg = body?.error || body?.detail || msg;
      } catch {
        /* response may not be JSON; fall back to status */
      }
      throw new Error(msg);
    }
    // A 200 that isn't actually a PDF (an HTML/JSON error page, or an empty
    // body when no document exists) would open as a blank tab. Surface a real
    // error instead so the caller can toast it.
    const ctype = res.headers.get("Content-Type") || "";
    if (!ctype.toLowerCase().includes("pdf")) {
      if (newTab) newTab.close();
      let msg = "No PDF is available for this yet.";
      try {
        const body = await res.clone().json();
        msg = body?.error || body?.detail || msg;
      } catch {
        /* not JSON — keep the friendly default */
      }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (newTab) {
      // Tab was successfully pre-opened — navigate it to the blob.
      newTab.location.href = url;
    } else {
      // Popup blocker prevented the pre-open. Fall back to a download
      // anchor click — the user's browser will at minimum download or
      // open the file inline; nothing silently fails.
      const a = document.createElement("a");
      a.href = url;
      a.download = (path.split("/").pop() || "agreement.pdf").replace(/[^a-zA-Z0-9._-]/g, "_");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // Free the object URL after a minute. The new tab has loaded by then;
    // keeping the blob around longer leaks memory.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (newTab) newTab.close();
    throw e;
  }
}

/**
 * Download an authenticated binary file (zip, pdf, etc.) via a blob + anchor
 * click. Unlike window.open(), this sends the auth header — window.open can't,
 * which is why authed download endpoints 404/401 when opened directly.
 */
export async function downloadAuthedFile(path, filename) {
  const token =
    localStorage.getItem("access") ||
    localStorage.getItem("bearer") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    null;
  if (!token) throw new Error("Not signed in. Refresh and try again.");
  const isJWT = !!localStorage.getItem("access");
  const authHeader = isJWT ? `Bearer ${token}` : `Token ${token}`;

  const res = await fetch(apiUrl(path), { headers: { Authorization: authHeader } });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error || body?.detail || msg;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || (path.split("/").pop() || "download");
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
